import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { ApiError, useApi } from "./useApi";
import { useShipToApi } from "./useShipToApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { assertArrayPayload } from "@utils/apiPayload";
import {
    categoryInUse,
    type CategoryResponse,
    type CreateCategoryRequest,
    type CreateCategoryResponse,
    type PopularCategory,
    type UpdateCategoryRequest,
} from "@models/category";
import type { FeaturedProduct } from "@models/product";
import {
    categoriesAtom,
    isFetchingCategoriesAtom,
    categoriesFetchErrorAtom,
    isProcessingCategoriesAtom,
} from "@/store/categoryAtoms";

const CATEGORIES_ENDPOINT = "/categories";

// Which getCategories read may publish to the tree and its status atoms (PR
// 369 review 1-3). Module-scope, like useProductApi's: the atoms are module-
// scope and written by every surface that reads categories, so only a
// sequence answers "has a newer read taken over?". Latest-only (`isLatest`).
const categoriesReads = createReadGuard();

// I4: the delete's in-use refusal is shown inside the delete dialog, so
// useApi's toast stays quiet for exactly that 409, recognised by code and
// counts (categoryInUse), never wording. Every other failure keeps its toast.
// A caller of deleteCategory MUST render the refusal itself.
const isCategoryInUseRefusal = (
    status: number | undefined,
    _errorCode: string | undefined,
    _hasApiBody: boolean,
    bodyText: string,
): boolean => status === 409 && categoryInUse(bodyText) !== undefined;

export const useCategoryApi = () => {
    const { callApi } = useApi();
    const { callShipToApi } = useShipToApi();
    // Session-bound: an answer landing after sign-out must not refill them.
    const categories = useAtomValue(categoriesAtom);
    const setCategories = useSessionSetAtom(categoriesAtom);
    const setIsFetching = useSetAtom(isFetchingCategoriesAtom);
    const setFetchError = useSessionSetAtom(categoriesFetchErrorAtom);
    const setIsProcessing = useSetAtom(isProcessingCategoriesAtom);

    const createCategory = useCallback(
        async (
            data: CreateCategoryRequest,
        ): Promise<CreateCategoryResponse> => {
            setIsProcessing(true);

            try {
                const response = await callApi<CreateCategoryResponse>(
                    CATEGORIES_ENDPOINT,
                    {
                        method: "POST",
                        body: JSON.stringify(data),
                    },
                );

                const newCategory: CategoryResponse = {
                    id: response.id,
                    name: data.name,
                    children: [],
                };

                if (data.parentId) {
                    const addToParent = (
                        cats: CategoryResponse[],
                    ): CategoryResponse[] => {
                        return cats.map((cat) => {
                            if (cat.id === data.parentId) {
                                return {
                                    ...cat,
                                    children: [...cat.children, newCategory],
                                };
                            }
                            if (cat.children.length > 0) {
                                return {
                                    ...cat,
                                    children: addToParent(cat.children),
                                };
                            }
                            return cat;
                        });
                    };
                    setCategories(addToParent(categories));
                } else {
                    setCategories([...categories, newCategory]);
                }

                return response;
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, categories, setCategories],
    );

    const updateCategory = useCallback(
        async (id: number, data: UpdateCategoryRequest): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${CATEGORIES_ENDPOINT}/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });

                // Keep the cached tree in sync so admin surfaces reflect the
                // edit without a refetch. This handles name/rank edits in place;
                // it intentionally does NOT move a node between parents — a
                // parentId change (reparent) is not reflected here and needs a
                // getCategories() refetch. (No UI reparents categories yet.)
                const applyUpdate = (
                    cats: CategoryResponse[],
                ): CategoryResponse[] =>
                    cats.map((cat) => {
                        if (cat.id === id) {
                            return {
                                ...cat,
                                name: data.name,
                                // Only overwrite the rank when the caller sent one
                                // (including null = unpin); omitting it must not
                                // clear an existing pin.
                                popularRank:
                                    data.popularRank !== undefined
                                        ? data.popularRank
                                        : cat.popularRank,
                                // Same guard for the featured product (null =
                                // none) so a body without the field leaves the
                                // cached value alone.
                                featuredProductId:
                                    data.featuredProductId !== undefined
                                        ? data.featuredProductId
                                        : cat.featuredProductId,
                            };
                        }
                        if (cat.children.length > 0) {
                            return {
                                ...cat,
                                children: applyUpdate(cat.children),
                            };
                        }
                        return cat;
                    });
                setCategories(applyUpdate(categories));
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, categories, setCategories],
    );

    // Resolves when the category is gone: the API answers 204 both for a
    // delete and for an id it does not have (idempotent), so either way the
    // node leaves the cached tree. A refusal rejects with useApi's ApiError
    // and leaves the tree alone. The node is removed from the tree as it
    // stands when the 204 lands (a functional update), not the tree this hook
    // rendered with, so a change made while the delete was out is kept.
    const deleteCategory = useCallback(
        async (id: number): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${CATEGORIES_ENDPOINT}/${id}`, {
                    method: "DELETE",
                    skipErrorNotification: isCategoryInUseRefusal,
                });

                const removeNode = (
                    cats: CategoryResponse[],
                ): CategoryResponse[] =>
                    cats
                        .filter((cat) => cat.id !== id)
                        .map((cat) =>
                            cat.children.length > 0
                                ? { ...cat, children: removeNode(cat.children) }
                                : cat,
                        );
                setCategories((current) => removeNode(current));
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, setCategories],
    );

    // `skipErrorNotification` is opt-in for a caller that renders its own
    // inline failed state (BrowseHome's chip row), mirroring
    // useProductApi.getPopular — one failure must not read as a toast AND a
    // panel. Left out, callApi's own default applies and the toast stays.
    // Shape-checked like useProductApi.getFeatured: a 2xx that is not an
    // array rejects, so the row shows its failed state instead of throwing
    // in `.map()` during render.
    const getPopular = useCallback(
        async (
            limit?: number,
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<PopularCategory[]> => {
            const endpoint = `${CATEGORIES_ENDPOINT}/popular`;
            const url = limit != null ? `${endpoint}?limit=${limit}` : endpoint;
            const rows = await callApi<PopularCategory[]>(url, {
                skipErrorNotification: options.skipErrorNotification,
            });
            assertArrayPayload(endpoint, rows);
            return rows;
        },
        [callApi],
    );

    // The product featured on a category's browse page (E4-S5, #183). A 404 is
    // the NORMAL "none" answer (no featured product, or it was deactivated), so
    // it resolves undefined; every other failure rejects so the caller can tell
    // "none" from "the read broke". skipErrorNotification: the banner is a
    // decorative surface that falls back to the first product, so neither the
    // 404 nor a real failure may toast over a working grid.
    const getFeaturedProduct = useCallback(
        async (categoryId: number): Promise<FeaturedProduct | undefined> => {
            try {
                return await callShipToApi<FeaturedProduct>(
                    `${CATEGORIES_ENDPOINT}/${categoryId}/featured-product`,
                    { skipErrorNotification: true },
                );
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) {
                    return undefined;
                }
                throw error;
            }
        },
        [callShipToApi],
    );

    // `skipErrorNotification` is opt-in, as on getPopular: for a caller that
    // renders this read's failure inline (the product form's category field).
    const getCategories = useCallback(
        async (
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<CategoryResponse[]> => {
            const { isLatest } = categoriesReads.begin();

            setIsFetching(true);
            setFetchError(false);

            try {
                const data = await callApi<CategoryResponse[]>(
                    CATEGORIES_ENDPOINT,
                    { skipErrorNotification: options.skipErrorNotification },
                );

                if (isLatest()) setCategories(data);

                return data;
            } catch (error) {
                if (isLatest()) setFetchError(true);
                throw error;
            } finally {
                if (isLatest()) setIsFetching(false);
            }
        },
        [callApi, setCategories, setIsFetching, setFetchError],
    );

    return {
        createCategory,
        updateCategory,
        deleteCategory,
        getPopular,
        getFeaturedProduct,
        getCategories,
    };
};
